/**
 * PublicLeaderboard - Public-facing leaderboard page
 *
 * Standalone page (no sidebar) for anonymous visitors to view rankings.
 * Features:
 * - Full-width layout with no sidebar
 * - URL params: ?highlight=username&strategy=TYPE
 * - Challenge type tabs
 * - Time period filters
 * - Debounced search
 * - Full pagination (First, Prev, Next, Last)
 * - Deaveris branding and marketing CTAs
 */
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useLeaderboard } from '../hooks/useLeaderboard'
import {
  LeaderboardTable,
  LeaderboardPagination,
  LeaderboardSearch,
  StrategyTabs,
  TimePeriodFilter
} from '../components/leaderboard'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  Trophy,
  Medal,
  UserPlus,
  LogIn,
  Sparkles,
  Target,
  TrendingUp,
  Star,
  Zap,
  ExternalLink
} from 'lucide-react'
import rankingsService from '../services/rankingsService'
import DeaverisLogo from '../assets/deaveris.svg'
import { useBranding } from '../stores/brandingStore'

// External URL for access requests
const REQUEST_ACCESS_URL = 'https://www.deaveris.com'

/**
 * Format percentile for display
 * "Top 0%" -> "Best" for clarity
 */
const formatPercentileDisplay = (rank, total) => {
  if (!rank || !total) return ''
  const percentile = ((total - rank) / total) * 100
  const topPercent = 100 - percentile
  if (topPercent <= 0) return 'Best'
  if (topPercent < 1) return 'Top 1%'
  return `Top ${Math.round(topPercent)}%`
}

const PublicLeaderboard = () => {
  const branding = useBranding()
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()

  // URL params
  const highlightUser = searchParams.get('highlight')
  const urlStrategy = searchParams.get('strategy')

  // Use the shared leaderboard hook
  const {
    selectedStrategy,
    selectedPeriod,
    leaderboard,
    loading,
    leaderboardLoading,
    currentPage,
    strategyTypes,
    loadError,
    searchTerm,
    debouncedSearchTerm,
    highlightedUserInfo,
    userPosition,
    pageSize,
    handleStrategyChange,
    handlePeriodChange,
    handlePageChange,
    handleSearchChange,
    clearSearch
  } = useLeaderboard({
    initialStrategy: urlStrategy,
    initialPeriod: 'OVERALL_BEST',
    highlightUser,
    userId: user?.id
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (loadError || strategyTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center text-center p-4">
        <Trophy className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Leaderboard Unavailable
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {loadError || 'No challenge types available'}
        </p>
        <Link to="/" className="btn-primary">
          Go to Home
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Header */}
      <header className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center">
            {/* Logo with Icon */}
            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-4 group">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center p-1.5 group-hover:bg-white/20 transition-colors">
                <img src={DeaverisLogo} alt="" className="w-full h-full" />
              </div>
              <span className="text-3xl font-bold text-white/90 group-hover:text-white transition-colors">
                {branding.name}
              </span>
            </Link>

            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy className="w-10 h-10 text-yellow-300" />
              <h1 className="text-3xl sm:text-4xl font-bold">Global Leaderboard</h1>
            </div>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              See how students around the world are mastering SAT prep.
              {!user && ' Join thousands of learners improving their scores every day.'}
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="flex items-center gap-2 text-white/80">
                <Star className="w-5 h-5 text-primary-400" />
                <span className="font-medium">
                  {leaderboard.pagination?.total?.toLocaleString() || '0'}+ Students
                </span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Zap className="w-5 h-5 text-primary-400" />
                <span className="font-medium">4 Challenge Types</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Target className="w-5 h-5 text-primary-400" />
                <span className="font-medium">Request Access</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Featured User Banner */}
        {highlightedUserInfo && (
          <div className="card bg-gradient-to-r from-primary-600 to-primary-700 text-white overflow-hidden">
            <div className="card-body py-4">
              <div className="flex items-center justify-center gap-3 text-center">
                <Trophy className="w-6 h-6 text-yellow-300" />
                <div>
                  <p className="text-sm text-white/80">Viewing ranking for</p>
                  <p className="text-xl font-bold">
                    {highlightedUserInfo.publicName} — #{highlightedUserInfo.rank} of{' '}
                    {highlightedUserInfo.total?.toLocaleString()}
                  </p>
                </div>
                <Trophy className="w-6 h-6 text-yellow-300" />
              </div>
            </div>
          </div>
        )}

        {/* User Position Banner for Authenticated Users */}
        {user && userPosition && userPosition.rank && !highlightedUserInfo && (
          <div className="card bg-gradient-to-r from-primary-600 to-indigo-700 text-white overflow-hidden">
            <div className="card-body py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-yellow-300" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80">Your current rank</p>
                    <p className="text-xl font-bold">
                      #{userPosition.rank.toLocaleString()} of{' '}
                      {userPosition.total?.toLocaleString() || '?'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/80">
                    {formatPercentileDisplay(userPosition.rank, userPosition.total)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Banner for Anonymous Users */}
        {!user && (
          <div className="card bg-gray-800 dark:bg-gray-800 text-white overflow-hidden border border-gray-700">
            <div className="card-body py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary-600/20 flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Ready to compete?</h3>
                    <p className="text-gray-300">
                      Request access and start climbing the leaderboard today!
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to="/login"
                    className="btn bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2 px-5 py-2.5"
                  >
                    <LogIn className="w-4 h-4" />
                    Log In
                  </Link>
                  <a
                    href={REQUEST_ACCESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2 px-5 py-2.5 font-semibold"
                  >
                    <UserPlus className="w-4 h-4" />
                    Request Access
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Box */}
        <div className="card">
          <div className="card-body py-4">
            <LeaderboardSearch
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onClear={clearSearch}
              debouncedTerm={debouncedSearchTerm}
              placeholder="Search for any player by username..."
            />
          </div>
        </div>

        {/* Challenge Type Tabs */}
        <div className="card">
          <div className="card-body p-3">
            <StrategyTabs
              strategies={strategyTypes}
              selectedStrategy={selectedStrategy}
              onStrategyChange={handleStrategyChange}
            />
          </div>
        </div>

        {/* Time Period Filter */}
        <div className="flex flex-wrap items-center gap-4">
          <TimePeriodFilter selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} />
        </div>

        {/* Leaderboard Card */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Medal className="w-5 h-5 text-yellow-500" />
                {rankingsService.getStrategyDisplayName(selectedStrategy)} Leaderboard
              </h2>
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                {leaderboard.pagination?.total > 0 && (
                  <span>{leaderboard.pagination.total.toLocaleString()} participants</span>
                )}
                <span>•</span>
                <span>{rankingsService.getTimePeriodDisplayName(selectedPeriod)}</span>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="md" />
              </div>
            ) : leaderboard.entries?.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No rankings yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Be the first to complete a challenge and claim the top spot!
                </p>
                {!user && (
                  <a
                    href={REQUEST_ACCESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    Request Access
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ) : (
              <>
                <LeaderboardTable
                  entries={leaderboard.entries}
                  currentUserId={user?.id}
                  startRank={
                    highlightUser
                      ? (leaderboard.pagination?.offset || 0) + 1
                      : currentPage * pageSize + 1
                  }
                  highlightedPublicName={highlightUser}
                  userPosition={userPosition}
                  topCount={15}
                />
                <LeaderboardPagination
                  currentPage={currentPage}
                  totalItems={leaderboard.pagination?.total || 0}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  hasMore={leaderboard.pagination?.hasMore}
                />
              </>
            )}
          </div>
        </div>

        {/* Bottom CTA for Anonymous Users */}
        {!user && (
          <div className="card bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <div className="card-body py-8 text-center">
              <TrendingUp className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Start Your SAT Journey Today
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
                Deaveris uses adaptive learning to personalize your SAT prep experience. Track your
                progress, compete with peers, and watch your scores improve.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={REQUEST_ACCESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-3 text-lg"
                >
                  Request Access
                  <ExternalLink className="w-5 h-5" />
                </a>
                <Link
                  to="/help/getting-started"
                  className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Branding */}
            <div className="text-center sm:text-left">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors group"
              >
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center p-1 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                  <img src={DeaverisLogo} alt="" className="w-full h-full" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                  {branding.name}
                </span>
              </Link>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{branding.tagline}</p>
            </div>

            {/* Links */}
            <div className="flex items-center gap-6 text-sm">
              <Link
                to="/help/getting-started"
                className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Getting Started
              </Link>
              <Link
                to="/legal/privacy"
                className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/legal/terms"
                className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Terms
              </Link>
            </div>

            {/* CTA */}
            {!user && (
              <div className="flex items-center gap-3">
                <a
                  href={REQUEST_ACCESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  Request access at deaveris.com
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>

          <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
            © {new Date().getFullYear()} Deaveris. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PublicLeaderboard
